import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Turn any axios error into a friendly, human message + a "server down" flag,
 * so the UI can tell "the server is unreachable" apart from "you typed
 * something invalid". Used by ErrorState and page-level catches.
 */
export const getFriendlyError = (err) => {
  // Request made but no response → network down / server unreachable / CORS.
  if (err?.code === 'ECONNABORTED') {
    return {
      isServerDown: true,
      title: 'This is taking too long',
      message: 'The server is taking too long to respond. Please try again in a moment.',
    };
  }
  if (!err?.response) {
    return {
      isServerDown: true,
      title: "We can't reach our servers",
      message:
        "We're having trouble connecting right now. Please check your internet connection and try again.",
    };
  }
  const status = err.response.status;
  if (status >= 500) {
    return {
      isServerDown: true,
      title: 'Our servers are having a moment',
      message: "Something went wrong on our side. We're on it — please try again shortly.",
    };
  }
  if (status === 404) {
    return { isServerDown: false, title: 'Not found', message: 'We could not find what you were looking for.' };
  }
  const raw = err.response?.data?.message;
  return {
    isServerDown: false,
    title: 'Something went wrong',
    message: (Array.isArray(raw) ? raw[0] : raw) || 'Please try again.',
  };
};

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Handle 401 — clear auth and redirect to login
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('registration-login')) {
        window.location.href = '/user-registration-login';
      }
    }
    // Attach a friendly message every page/component can rely on.
    const friendly = getFriendlyError(err);
    err.isServerDown = friendly.isServerDown;
    err.userMessage = friendly.message;
    err.userTitle = friendly.title;
    return Promise.reject(err);
  }
);

export default api;
